import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { DashboardSkeleton } from '../components/ui/LoadingState';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import { NotificationContainer } from '../components/ui/NotificationToast';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const AllActivities = () => {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [filterType, setFilterType] = useState('all'); // State for filtering by activity type
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const ITEMS_PER_PAGE = 10;

  const { user } = useSelector((state) => state.auth);
  const isPic = user?.role === 'pic' || user?.role === 'department_pic';

  const addNotification = (message, type) => {
    setNotifications(prev => [...prev, { id: Date.now(), message, type }]);
  };

  const handleFilterChange = (newType) => {
    setFilterType(newType);
    setPage(0);
    setActivities([]);
    setHasMore(true);
  };

  useEffect(() => {
    const fetchAllActivities = async () => {
      setIsLoading(true);
      try {
        const from = page * ITEMS_PER_PAGE;
        const to = from + ITEMS_PER_PAGE - 1;

        let query = supabase
          .from('activities')
          .select(`
            *,
            assets ( product_name, asset_tag, current_department_id )
          `)
          .order('created_at', { ascending: false });

        // Apply department filter for PIC before pagination
        if (isPic && user?.department_id) {
          query = query.eq('assets.current_department_id', user.department_id);
        }

        // Apply activity type filter
        if (filterType !== 'all') {
          query = query.eq('type', filterType);
        }

        // Apply pagination
        query = query.range(from, to);

        

        const { data, error } = await query;

        const filteredData = data;

        if (error) throw error;

        // Set hasMore based on returned data length
        if (data.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        } else {
          setHasMore(true);
        }

        

        // Get user IDs from activities
        const userIds = [...new Set(filteredData.map(a => a.user_id).filter(Boolean))];

        // Fetch profiles for the user IDs
        let profilesMap = {};
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('profiles')
            .select('id, full_name, email')
            .in('id', userIds);
          
          if (profilesError) throw profilesError;

          profilesMap = profilesData.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {});
        }

        const formattedActivities = filteredData.map(activity => ({
          id: activity.id,
          type: activity.type,
          description: activity.description,
          user: profilesMap[activity.user_id] ? (profilesMap[activity.user_id].full_name || profilesMap[activity.user_id].email) : 'Unknown User',
          timestamp: new Date(activity.created_at),
          assetId: activity.assets?.asset_tag || activity.asset_tag,
        }));

        setActivities(prev => page === 0 ? formattedActivities : [...prev, ...formattedActivities]);
      } catch (error) {
        console.error('Error fetching all activities:', error);
        addNotification(t('activities.fetch_error', { message: error.message }), 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      fetchAllActivities();
    }
  }, [filterType, user, isPic, page]); // Refetch when filters or page changes

  const getActivityIcon = (type) => {
    switch (type) {
      case 'asset_added':
        return 'Plus';
      case 'asset_updated':
        return 'Edit';
      case 'asset_deleted':
        return 'Trash';
      case 'asset_assigned':
        return 'ArrowRight';
      case 'asset_image_updated':
        return 'Image';
      case 'maintenance_logged':
        return 'Wrench';
      case 'asset_checked_out':
        return 'ArrowRight';
      case 'asset_checked_in':
        return 'ArrowLeft';
      case 'asset_retired':
        return 'Archive';
      default:
        return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'asset_added':
        return 'text-success';
      case 'asset_updated':
        return 'text-accent';
      case 'asset_deleted':
        return 'text-destructive';
      case 'asset_assigned':
        return 'text-indigo-500';
      case 'asset_image_updated':
        return 'text-blue-500';
      case 'maintenance_logged':
        return 'text-primary';
      case 'asset_checked_out':
        return 'text-warning';
      case 'asset_checked_in':
        return 'text-success';
      case 'asset_retired':
        return 'text-muted-foreground';
      default:
        return 'text-foreground';
    }
  };

  const activityTypes = [
    { value: 'all', label: t('activities.all') },
    { value: 'asset_added', label: t('activities.asset_added') },
    { value: 'asset_updated', label: t('activities.asset_updated') },
    { value: 'asset_deleted', label: t('activities.asset_deleted') },
    { value: 'asset_assigned', label: t('activities.asset_assigned') },
    { value: 'maintenance_logged', label: t('activities.maintenance_logged') },
    { value: 'asset_retired', label: t('activities.asset_retired') },
  ];


  if (isLoading && page === 0 && activities.length === 0) {
    return (
      <div>
        <DashboardSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-foreground">{t('activities.title')}</h1>
      <p className="text-muted-foreground">{t('activities.subtitle')}</p>

      {/* Filter by Type */}
      <div className="flex items-center gap-2">
        <label htmlFor="activityTypeFilter" className="text-sm font-medium text-muted-foreground">{t('activities.filter_by_type')}</label>
        <select
          id="activityTypeFilter"
          className="px-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          value={filterType}
          onChange={(e) => handleFilterChange(e.target.value)}
        >
          {activityTypes.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
        {activities.length === 0 && !isLoading ? (
          <div className="p-12 text-center text-muted-foreground">
            <Icon name="SearchX" size={48} className="mx-auto mb-4 opacity-20" />
            <p>{t('activities.no_results')}</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {activities.map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-4 hover:bg-muted/50 transition-colors">
                <div className={`p-2 rounded-full bg-muted ${getActivityColor(activity.type)}`}>
                  <Icon name={getActivityIcon(activity.type)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{activity.description}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <span className="text-xs text-muted-foreground">{t('activities.by_user', { user: activity.user })}</span>
                    <span className="text-xs text-muted-foreground">•</span>
                    <span className="text-xs text-muted-foreground">{format(activity.timestamp, 'MMM dd, yyyy HH:mm')}</span>
                  </div>
                  {activity.assetId && (
                    <p className="text-xs text-accent mt-1">{t('activities.asset_id', { assetId: activity.assetId })}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <Button 
            variant="outline" 
            onClick={() => setPage(prev => prev + 1)}
            loading={isLoading}
          >
            Show 10 More
          </Button>
        </div>
      )}

      <NotificationContainer notifications={notifications} onRemove={(id) => setNotifications(prev => prev.filter(n => n.id !== id))} />
    </div>
  );
};

export default AllActivities;
